import { useState } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  styled,
  Typography,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import FormProvider, { RHFSelect, RHFTextField } from 'src/components/hook-form';
import * as Yup from 'yup';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import SmsCountUpdateCard from './SmsCountUpdateCard';
import CampaignDateTimeUpdateCard from './CampaignDateTimeUpdateCard';
import UserSelectCard from './UserSelectCard';
import SmsAdminSettingInstructions from './SmsAdminSettingInstructions';
import { axiosPostCall } from 'src/services/apisCall';
import { renderErrorMessage, renderSuccessMessage } from 'src/lib/toastMessage';
import { checkButtonAccess } from 'src/utils/common';

const InputContainer = styled('div')(() => ({
  display: 'flex',
  width: '100%',
  gap: '20px',
}));

const InputInnerContainer = styled('div')(() => ({
  flex: 1,
}));

// Pending is removed completely
const ActionTypeOptions = [
  { label: 'Campaign Date & Time', value: 'campaignDateTime' },
  { label: 'Update Delivered Count (D)', value: 'delivered' },
  { label: 'Update Submitted Count (S)', value: 'submitted' },
  { label: 'Update Failed Count (F)', value: 'failed' },
];

const formatDateTime = (val) => {
  if (!val) return null;
  if (typeof val?.toISOString === 'function') {
    return val.toISOString();
  }
  const date = new Date(val);
  return isNaN(date.getTime()) ? null : date.toISOString();
};

const COUNT_ACTION_TYPES = ['delivered', 'submitted', 'failed'];

export default function SmsAdminSettingForm() {
  const theme = useTheme();
  const [loader, setLoader] = useState(false);
  const [resetloader, setResetLoader] = useState(false);

  // Global count mode: 'count' | 'percent'
  const [countMode, setCountMode] = useState('count');

  // Selected User
  const [selectedUser, setSelectedUser] = useState(null);
  const [userError, setUserError] = useState('');

  // Global "Include All Campaign Records" flag
  const [updateExistingRecords, setUpdateExistingRecords] = useState(false);

  const optionalNumber = Yup.number()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .nullable();

  const countField = (fieldName, actionKey) =>
    optionalNumber.when('actionType', {
      is: (val) => val === actionKey,
      then: (s) => {
        let base = s.required(`${fieldName} is required`).typeError('Must be a number').min(0);
        if (countMode === 'percent') {
          base = base.max(100, `${fieldName} cannot exceed 100 in percent mode`);
        }
        return base;
      },
      otherwise: (s) => s.nullable(),
    });

  const schema = Yup.object().shape({
    campaignIds: Yup.string().required('At least one Campaign ID is required'),
    actionType: Yup.string().required('Action type is required'),
    deliveredCount: countField('Delivered count', 'delivered'),
    submittedCount: countField('Submitted count', 'submitted'),
    failedCount: countField('Failed count', 'failed'),
    submittedDeliveredCount: optionalNumber.min(0),
    submittedFailedCount: optionalNumber.min(0),
    submittedDndCount: optionalNumber.min(0),
    campaignDateTime: Yup.mixed().nullable(),
    messageSendTime: Yup.mixed().nullable(),
    deliveryTime: Yup.mixed().nullable(),
  });

  const defaultValues = {
    campaignIds: '',
    actionType: '',
    deliveredCount: '',
    adjustedDeliveredCount: [],
    submittedCount: '',
    submittedDeliveredCount: '',
    submittedFailedCount: '',
    submittedDndCount: '',
    failedCount: '',
    adjustedFailedCount: [],
    campaignDateTime: null,
    messageSendTime: null,
    deliveryTime: null,
  };

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    control,
  } = methods;

  const actionTypeValue = useWatch({
    control,
    name: 'actionType',
  }) || '';

  const hasCountActionSelected = COUNT_ACTION_TYPES.includes(actionTypeValue);

  const handleReset = () => {
    setResetLoader(true);
    reset();
    setSelectedUser(null);
    setUserError('');
    setCountMode('count');
    setUpdateExistingRecords(false);
    setTimeout(() => {
      setResetLoader(false);
    }, 500);
  };

  // ── Payload Builder ──────────────────────────────────────────────────────
  const buildPayload = (data) => {
    const campaignIds = data.campaignIds
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);

    const payload = { campaignIds, updateExistingRecords };

    if (actionTypeValue === 'delivered') {
      payload.D_required = {
        current: 'D',
        count: Number(data.deliveredCount),
        countMode,
        adjustedTo: data.adjustedDeliveredCount?.length ? data.adjustedDeliveredCount : ['P', 'F', 'DND'],
      };
    }

    if (actionTypeValue === 'submitted') {
      // For Submitted: No adjustedTo is sent; distribution is specified via delivered, failed, dnd
      payload.S_required = {
        current: 'S',
        count: Number(data.submittedCount),
        countMode,
        delivered: data.submittedDeliveredCount ? Number(data.submittedDeliveredCount) : 0,
        failed: data.submittedFailedCount ? Number(data.submittedFailedCount) : 0,
        dnd: data.submittedDndCount ? Number(data.submittedDndCount) : 0,
      };
    }

    if (actionTypeValue === 'failed') {
      payload.F_required = {
        current: 'F',
        count: Number(data.failedCount),
        countMode,
        adjustedTo: data.adjustedFailedCount?.length ? data.adjustedFailedCount : ['P', 'D', 'DND'],
      };
    }

    if (actionTypeValue === 'campaignDateTime') {
      payload.DateTime_required = {
        campaignDateTime: formatDateTime(data.campaignDateTime),
        messageSendTime: formatDateTime(data.messageSendTime),
        deliveryTime: formatDateTime(data.deliveryTime),
      };
    }

    return payload;
  };

  const onSubmit = async (data) => {
    if (!selectedUser) {
      setUserError('User is required. Please select a user.');
      return;
    }
    try {
      setLoader(true);
      const payload = buildPayload(data);
      payload.selectedUserId = selectedUser.userId;
      console.log('SMS Admin Setting Payload:', JSON.stringify(payload, null, 2));
      const { message } = await axiosPostCall('smsAdminSettingUpdate', payload);
      renderSuccessMessage(message || 'SMS Admin setting updated successfully');
      handleReset();
    } catch (error) {
      console.log('error in sms admin setting update', error);
      renderErrorMessage(error?.message || 'Network error!');
    } finally {
      setLoader(false);
    }
  };

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
        SMS Admin Setting
      </Typography>

      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        {/* Row 1 — User Selection */}
        <Box sx={{ mb: 3 }}>
          <UserSelectCard
            selectedUser={selectedUser}
            onUserSelect={(user) => {
              setSelectedUser(user);
              if (user) setUserError('');
            }}
            error={userError}
          />
        </Box>

        {/* Row 2 — Campaign IDs, Action Type (Single Select) & Count Mode */}
        <InputContainer sx={{ mb: 3 }}>
          <InputInnerContainer>
            <RHFTextField
              name="campaignIds"
              label="Campaign IDs"
              placeholder="e.g. 123484, 123485"
              helperText="Separate multiple campaign IDs with commas"
              fullWidth
            />
          </InputInnerContainer>

          <InputInnerContainer>
            <RHFSelect
              name="actionType"
              label="Action Type"
              size="medium"
            >
              <MenuItem value="" disabled>
                Select Action Type
              </MenuItem>
              {ActionTypeOptions.map(({ label, value }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </RHFSelect>
          </InputInnerContainer>

          {/* Count Mode Toggle (visible only when a count action is selected) */}
          {hasCountActionSelected ? (
            <InputInnerContainer sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                Count Input Mode
              </Typography>
              <ToggleButtonGroup
                value={countMode}
                exclusive
                onChange={(_, next) => {
                  if (next) setCountMode(next);
                }}
                size="small"
              >
                <ToggleButton value="count" sx={{ px: 2 }}>
                  Count
                </ToggleButton>
                <ToggleButton value="percent" sx={{ px: 2 }}>
                  Percentage (%)
                </ToggleButton>
              </ToggleButtonGroup>
            </InputInnerContainer>
          ) : (
            <InputInnerContainer />
          )}
        </InputContainer>

        {/* Include All Campaign Records checkbox (just like OBD) */}
        {Boolean(actionTypeValue) && (
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              display: 'inline-flex',
              alignItems: 'center',
              mb: 3,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={updateExistingRecords}
                  onChange={(e) => setUpdateExistingRecords(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2">Include All Campaign Records</Typography>
              }
              sx={{ m: 0 }}
            />
          </Box>
        )}

        {/* Action card dynamically displayed based on selected single action */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          {actionTypeValue === 'campaignDateTime' && (
            <CampaignDateTimeUpdateCard key="campaignDateTime" />
          )}

          {hasCountActionSelected && (
            <SmsCountUpdateCard
              key={actionTypeValue}
              actionType={actionTypeValue}
              countMode={countMode}
            />
          )}
        </Box>

        {/* Action buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
          <LoadingButton
            variant="outlined"
            color="inherit"
            onClick={handleReset}
            loading={resetloader}
            sx={{ width: '100px' }}
          >
            Reset
          </LoadingButton>

          {checkButtonAccess('smsAdminSetting', 'w') !== false && (
            <LoadingButton
              type="submit"
              variant="contained"
              loading={loader}
              sx={{ width: '120px' }}
            >
              Submit
            </LoadingButton>
          )}
        </Box>
      </FormProvider>

      <SmsAdminSettingInstructions />
    </Box>
  );
}
